import { API_CONFIG } from '../config/api';

class ApiService {
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      ...otherOptions
    } = options;

    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: 'include', // Always include credentials
      ...otherOptions,
    };

    // Add body for POST/PUT/DELETE requests
    if (body && (method === 'POST' || method === 'PUT' || method === 'DELETE')) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, config);
      
      // Check for LoggedInYN header in response (try exact case first)
      let loggedInYn = response.headers.get('LoggedInYN');
      if (loggedInYn === null) {
        // Try other case variations
        loggedInYn = response.headers.get('loggedinyn') || 
                    response.headers.get('loggedInYn') || 
                    response.headers.get('LoggedInYn') || 
                    response.headers.get('LOGGEDINYN');
      }
      
      if (loggedInYn !== null) {
        console.log(`API Call to ${endpoint}: LoggedInYN header = ${loggedInYn}`);
        
        // You can add additional logic here based on the LoggedInYN value
        // For example, redirect to login if LoggedInYN is '0', 'N', or 'false'
        if (loggedInYn === '0' || loggedInYn.toLowerCase() === 'n' || loggedInYn.toLowerCase() === 'false') {
          console.warn('User session expired or invalid. Consider redirecting to login.');
          // Optionally trigger a logout or redirect
          // window.location.href = '/login';
        } else if (loggedInYn === '1' || loggedInYn.toLowerCase() === 'y' || loggedInYn.toLowerCase() === 'true') {
          console.log('User session is valid.');
        }
      } else {
        console.log(`API Call to ${endpoint}: No LoggedInYN header found`);
        // Log all headers for debugging (note: CORS may limit visible headers)
        console.log('Available headers:', Array.from(response.headers.entries()));
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle different response types
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  async delete(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE', body });
  }

  // File upload method
  async uploadFile(endpoint, formData, options = {}) {
    const config = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      ...options,
    };

    // Remove Content-Type header to let browser set it with boundary
    delete config.headers?.['Content-Type'];

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, config);
      
      // Check for LoggedInYN header in file upload responses too
      let loggedInYn = response.headers.get('LoggedInYN');
      if (loggedInYn === null) {
        // Try other case variations
        loggedInYn = response.headers.get('loggedinyn') || 
                    response.headers.get('loggedInYn') || 
                    response.headers.get('LoggedInYn') || 
                    response.headers.get('LOGGEDINYN');
      }
      
      if (loggedInYn !== null) {
        console.log(`File Upload to ${endpoint}: LoggedInYN header = ${loggedInYn}`);
        
        if (loggedInYn === '0' || loggedInYn.toLowerCase() === 'n' || loggedInYn.toLowerCase() === 'false') {
          console.warn('User session expired or invalid during file upload. Consider redirecting to login.');
        } else if (loggedInYn === '1' || loggedInYn.toLowerCase() === 'y' || loggedInYn.toLowerCase() === 'true') {
          console.log('User session is valid.');
        }
      } else {
        console.log(`File Upload to ${endpoint}: No LoggedInYN header found`);
        // Log all headers for debugging (note: CORS may limit visible headers)
        console.log('Available headers:', Array.from(response.headers.entries()));
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      console.error(`File upload failed for ${endpoint}:`, error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
